import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { RestaurantAnnouncement } from '@/types';
import { lockBodyScroll } from '@/lib/bodyScrollLock';
import { sanitizeAnnouncementHtml } from './sanitizeHtml';
import { localizedAnnouncement } from '@/lib/localizeChrome';

interface Props {
  announcement: RestaurantAnnouncement;
  /** Restaurant logo shown atop the popup when the announcement has no image. */
  logoUrl?: string | null;
}

const OPEN_DELAY_MS = 600;

export default function AnnouncementModal({ announcement: source, logoUrl }: Props) {
  const { t, i18n } = useTranslation();
  // DF-0428 — the popup in the customer's language, French as fallback.
  const announcement = localizedAnnouncement(source, i18n.language);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setOpen(true), OPEN_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [announcement.id]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const releaseScroll = lockBodyScroll();
    return () => {
      document.removeEventListener('keydown', onKey);
      releaseScroll();
    };
  }, [open]);

  const dismiss = () => setOpen(false);
  const safeLead = announcement.lead ? sanitizeAnnouncementHtml(announcement.lead) : '';
  const safeClosing = announcement.closing ? sanitizeAnnouncementHtml(announcement.closing) : '';

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 sm:p-6 bg-ink/75 backdrop-blur-[6px] animate-[announceOverlay_180ms_ease-out]">
      <div className="absolute inset-0" onClick={dismiss} aria-hidden="true" />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={announcement.headline}
        className="relative bg-paper border-2 border-ink w-full max-w-[440px] max-h-[calc(100dvh-32px)] overflow-y-auto animate-[announceIn_320ms_cubic-bezier(0.34,1.4,0.64,1)]"
        style={{ boxShadow: 'var(--shadow-stamp-lg), 0 30px 60px -20px rgba(0,0,0,0.45)' }}
      >
        {announcement.image && (
          <div className="relative bg-ash overflow-hidden border-b-2 border-ink aspect-[16/10]">
            <img
              src={announcement.image}
              alt=""
              className="absolute inset-0 w-full h-full object-cover animate-[announceImg_9s_ease-in-out_infinite]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/45 via-ink/0 to-transparent" aria-hidden="true" />
            <span
              aria-hidden="true"
              className="absolute -bottom-3 right-4 inline-flex items-center gap-1.5 bg-hot-pink text-[var(--on-accent)] font-display text-[14px] uppercase tracking-[0.1em] px-3 py-1.5 border-2 border-ink leading-none animate-[announceStamp_700ms_cubic-bezier(0.34,1.56,0.64,1)_both]"
              style={{ transform: 'rotate(-6deg)', boxShadow: 'var(--shadow-stamp-sm)' }}
            >
              <span>★</span>
              <span>{t('announcement.welcome')}</span>
            </span>
          </div>
        )}

        {!announcement.image && logoUrl && (
          <div className="pt-7 sm:pt-8 -mb-1 flex justify-center animate-[announceRise_420ms_ease-out_both]">
            <img src={logoUrl} alt="" className="h-20 max-w-[60%] object-contain" />
          </div>
        )}

        <div className="px-6 pt-7 pb-5 sm:px-8 sm:pt-8 sm:pb-6 flex flex-col items-center text-center">
          {/* welcome zone */}
          <div className="flex flex-col items-center gap-2 animate-[announceRise_420ms_ease-out_60ms_both]">
            {announcement.greeting && (
              <span className="inline-flex items-center gap-1.5 bg-mint border-2 border-ink rounded-full px-3 py-1 font-display text-[12px] text-[var(--on-primary)] leading-none shadow-stamp-sm">
                {announcement.greeting}
              </span>
            )}
            <h2 className="font-display text-ink text-[30px] sm:text-[36px] leading-[1.02] m-0 max-w-[16ch]">
              {announcement.headline}
            </h2>
          </div>

          {/* about zone */}
          {(announcement.lead || announcement.tagline) && (
            <div className="flex flex-col items-center gap-1.5 mt-4 animate-[announceRise_420ms_ease-out_140ms_both]">
              {announcement.lead && (
                <p className="font-display text-[14px] sm:text-[15px] text-ink leading-[1.35] m-0" dangerouslySetInnerHTML={{ __html: safeLead }} />
              )}
              {announcement.tagline && (
                <p className="font-display text-[12px] sm:text-[13px] text-ink/65 leading-[1.3] m-0">
                  {announcement.tagline}
                </p>
              )}
            </div>
          )}

          {/* call-to-action zone */}
          <div className="w-full mt-5 pt-4 border-t border-dashed border-ink/20 flex flex-col items-stretch gap-2.5 animate-[announceRise_420ms_ease-out_220ms_both]">
            {announcement.closing && (
              <p className="font-display text-mint-deep text-[15px] sm:text-[16px] leading-[1.3] m-0 mb-1 inline-flex items-center justify-center gap-1.5">
                <span aria-hidden="true">↓</span>
                <span dangerouslySetInnerHTML={{ __html: safeClosing }} />
              </p>
            )}
            <button
              type="button"
              onClick={dismiss}
              className="inline-flex items-center justify-center bg-hot-pink border-2 border-ink rounded-btn px-5 py-2.5 font-display text-[var(--on-accent)] text-[15px] shadow-stamp-sm transition-shadow duration-200 hover:shadow-stamp active:scale-[0.98]"
            >
              {t('common.close')}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={dismiss}
          aria-label={t('announcement.close')}
          className="absolute top-3 end-3 w-9 h-9 inline-flex items-center justify-center bg-paper/95 border-2 border-ink rounded-full text-ink hover:bg-mint transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-4 h-4" aria-hidden="true">
            <path
              d="M6 6 L18 18 M18 6 L6 18"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </button>
      </div>

      <style>{`
        @keyframes announceOverlay {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes announceIn {
          from { opacity: 0; transform: translateY(28px) scale(0.94); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes announceRise {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes announceImg {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.06); }
        }
        @keyframes announceStamp {
          0%   { opacity: 0; transform: rotate(-32deg) scale(0.4); }
          70%  { opacity: 1; transform: rotate(-3deg) scale(1.12); }
          100% { opacity: 1; transform: rotate(-6deg) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-\\[announceImg_9s_ease-in-out_infinite\\],
          .animate-\\[announceStamp_700ms_cubic-bezier\\(0\\.34\\,1\\.56\\,0\\.64\\,1\\)_both\\],
          .animate-\\[announceRise_420ms_ease-out_60ms_both\\],
          .animate-\\[announceRise_420ms_ease-out_140ms_both\\],
          .animate-\\[announceRise_420ms_ease-out_220ms_both\\] {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}
